import React, { useContext, useEffect, useState } from 'react';
import { ContentSection } from '../../../components/Layout/ContentSection';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import ExportButton from '../../../components/Button/ExportButton';
import { Container, InventoryCheck } from '../../InventoryCheckView';
import { Badge, FormGroup, Input, Label } from 'reactstrap';
import convertDateToDateTimeLocalString from '@/utils/convertDateToDateTimeLocalString';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { FaQrcode } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { OverlayPopover } from '../../../components/OverlayPopover';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../../providers/ViewerContextProvider';
import { LayoutStoreInstance } from '../../../LayoutStore';
import {
  exportJsonAsCsv,
  exportJsonAsExcel,
  buildMissingContainersExportRows,
  fetchConsumerOriginMap,
} from './inventoryExport';

interface ContainerType {
  id: number;
  name: string;
}

const renderCell = (content: any, asBadge = false, badgeColor = 'primary') => {
  if (asBadge) {
    return (
      <Badge color={badgeColor} pill>
        <span className="text-white">{content}</span>
      </Badge>
    );
  }

  return (
    <div style={{ wordWrap: 'break-word', display: 'block' }}>
      <span>{content}</span>
    </div>
  );
};

const renderDate = (date: string) => {
  const dateFormatted = date
    ? new Date(date).toLocaleDateString(
        i18n.language,
        getDateFormatOptions(true, i18n.language),
      )
    : '-';

  return <div>{dateFormatted}</div>;
};

const renderContainerName = (id: string, name: string) => {
  return (
    <Link to={`/container/${id}`}>
      {name}
      <OverlayPopover
        body={
          <img alt="QR code for container" src={getContainerQrCodeImgUrl(id)} />
        }
        header={i18n.t('InventoryCheck:qrCodeFor', { name })}
      >
        <FaQrcode className="ms-1" style={{ cursor: 'pointer' }} />
      </OverlayPopover>
    </Link>
  );
};

const renderStatus = (status: boolean) => {
  const statusText = status
    ? i18n.t('InventoryCheck:confirmed')
    : i18n.t('InventoryCheck:stillLost');
  const color = status ? 'primary' : 'secondary';

  return (
    <Badge color={color} pill>
      <span className="text-white">{statusText}</span>
    </Badge>
  );
};

const containerStatusEnum = {
  All: 'all',
  Lost: 'lost',
  Found: 'found',
};

type InventoryCheckTabLostBowlsProps = {
  vytalStoreGuid: string;
  isLoading: boolean;
  containerTypes: ContainerType[];
  inventoryChecks: InventoryCheck[];
};

const InventoryCheckTabLostBowls = ({
  vytalStoreGuid,
  containerTypes,
  inventoryChecks,
  isLoading,
}: InventoryCheckTabLostBowlsProps) => {
  const viewer = useContext(ViewerContext);
  const showPartnerData = CanAccessPartnerData(viewer);

  const currentDate = new Date();

  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [currentContainers, setCurrentContainers] = useState<
    Container[] | undefined
  >([]);
  const [selectedContainerStatus, setSelectedContainerStatus] =
    useState<string>(containerStatusEnum.All);
  const [lastInventory, setLastInventory] = useState<InventoryCheck | null>(
    null,
  );

  const getMissingContainersFilteredByStatus = (
    currentContainerStatus: string,
    missingContainers: Container[],
  ) => {
    return missingContainers
      ?.filter((c) => {
        // show only found
        if (currentContainerStatus === containerStatusEnum.Found) {
          return c.seenNext !== null;
        }

        // show only lost
        if (currentContainerStatus === containerStatusEnum.Lost) {
          return c.seenNext === null;
        }

        // all
        return true;
      })
      .sort(
        (a, b) =>
          new Date(b.lastSeenAtStoreBeforeExpiration).getTime() -
          new Date(a.lastSeenAtStoreBeforeExpiration).getTime(),
      );
  };

  function getMissingContainerStatus(
    container: Container,
    expirationDate: string,
  ) {
    if (container.seenNext != null && container.seenNext > expirationDate) {
      return i18n.t('InventoryCheck:recovered');
    }
    if (container.seenNext != null) {
      return i18n.t('InventoryCheck:confirmed');
    }
    return i18n.t('InventoryCheck:stillLost');
  }

  const toJson = async (
    viewer: any,
    inventorychecks: InventoryCheck[],
    containerTypes: ContainerType[],
  ) => {
    // expired and billable
    let containers: any[] = [];

    const inventoryChecksFiltered = inventorychecks;

    for (const inventoryCheck of inventoryChecksFiltered) {
      const consumerOriginMap = await fetchConsumerOriginMap(inventoryCheck.id);
      const filteredMissingContainers = getMissingContainersFilteredByStatus(
        selectedContainerStatus,
        inventoryCheck.missingContainers,
      );
      containers = containers.concat(
        buildMissingContainersExportRows(
          viewer,
          inventoryCheck,
          containerTypes,
          filteredMissingContainers.filter((c) => c.isBillable),
          consumerOriginMap,
        ),
      );

      containers = containers.concat(
        buildMissingContainersExportRows(
          viewer,
          inventoryCheck,
          containerTypes,
          filteredMissingContainers.filter((c) => !c.isBillable),
          consumerOriginMap,
        ),
      );
    }

    return [...containers].sort(
      (a, b) =>
        a[i18n.t('InventoryCheck:typeId')] - b[i18n.t('InventoryCheck:typeId')],
    );
  };

  const exportCSV = async (viewer: any) => {
    if (lastInventory) {
      const json = await toJson(viewer, [lastInventory], containerTypes);

      if (json.length === 0) {
        LayoutStoreInstance.setError(
          i18n.t('InventoryCheck:exportNoContainers'),
        );
        return;
      }
      exportJsonAsCsv(
        json,
        `lost_bowls_since_last_inventory_${vytalStoreGuid}_${new Date().getTime()}.csv`,
      );
    }
  };

  const exportExcel = async (viewer: any) => {
    if (lastInventory) {
      const json = await toJson(viewer, [lastInventory], containerTypes);

      if (json.length === 0) {
        LayoutStoreInstance.setError(
          i18n.t('InventoryCheck:exportNoContainers'),
        );
        return;
      }
      exportJsonAsExcel(
        json,
        `lost_bowls_since_last_inventory_${vytalStoreGuid}`,
      );
    }
  };

  const getContainersFiltered = (containerStatus: string) => {
    [...inventoryChecks].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    const lastInventoryCheck = inventoryChecks[inventoryChecks.length - 1];
    if (!lastInventoryCheck) {
      return {};
    }

    const missingContainers = lastInventoryCheck?.missingContainers;

    const missingContainersFiltered = getMissingContainersFilteredByStatus(
      containerStatus,
      missingContainers,
    );

    return { missingContainersFiltered, lastInventoryCheck };
  };

  const updateCurrentContainers = () => {
    const { missingContainersFiltered, lastInventoryCheck } =
      getContainersFiltered(selectedContainerStatus);

    if (lastInventoryCheck) {
      setLastInventory(lastInventoryCheck);
      setStartDate(new Date(lastInventoryCheck.expirationDate));
    }

    if (missingContainersFiltered) {
      setCurrentContainers(missingContainersFiltered);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      updateCurrentContainers();
    }
  }, [inventoryChecks, isLoading, selectedContainerStatus, endDate]);

  const { missingContainersFiltered: missinContainersTotal } =
    getContainersFiltered(containerStatusEnum.All);
  const { missingContainersFiltered: missinContainersLost } =
    getContainersFiltered(containerStatusEnum.Lost);
  const { missingContainersFiltered: missinContainersFound } =
    getContainersFiltered(containerStatusEnum.Found);

  const total = missinContainersTotal?.length || 0;
  const missing = missinContainersLost?.length || 0;
  const recovered = missinContainersFound?.length || 0;

  const InventoryIsRunning =
    lastInventory &&
    [1, 4, 5].includes(lastInventory?.status) &&
    !lastInventory.isExpired;

  return (
    <ContentSection
      title={i18n.t('InventoryCheck:lostBowls')}
      collapsable={false}
      key="content"
    >
      {InventoryIsRunning && i18n.t('InventoryCheck:isRunningText')}
      {!InventoryIsRunning && (
        <>
          <div>
            <FormGroup>
              <Label>{i18n.t('InventoryCheck:currentStatus')}</Label>

              <Input
                type="select"
                onChange={(e) => {
                  setSelectedContainerStatus(e.currentTarget.value);
                }}
              >
                <option value="all">
                  {i18n.t('InventoryCheck:filterAll')} ({total})
                </option>
                <option value="lost">
                  {i18n.t('InventoryCheck:filterLost')} ({missing})
                </option>
                <option value="found">
                  {i18n.t('InventoryCheck:filterFound')} ({recovered})
                </option>
              </Input>
            </FormGroup>

            <FormGroup>
              <Label for="startDate" className="form-label">
                {i18n.t('InventoryCheck:expiryDate')}
              </Label>

              <Input
                type="datetime-local"
                title={i18n.t('InventoryCheck:startDateTitle')}
                value={convertDateToDateTimeLocalString(startDate)}
                disabled={true}
              />
            </FormGroup>

            <FormGroup>
              <Label for="endDate" className="form-label">
                {i18n.t('Common:endDate')}
              </Label>

              <Input
                type="datetime-local"
                value={convertDateToDateTimeLocalString(endDate)}
                onChange={(event) => {
                  setEndDate(new Date(event.target.value));
                }}
              />
            </FormGroup>
          </div>

          <div className="mb-2 pt-3">
            <ExportButton
              disabled={lastInventory === null}
              csvCallback={() => {
                exportCSV(viewer);
              }}
              excelCallback={() => {
                exportExcel(viewer);
              }}
            />
          </div>

          <DataTable
            value={currentContainers}
            dataKey="id"
            responsiveLayout="scroll"
            loading={isLoading}
            className="p-datatable-striped"
            paginator
            rows={10}
            totalRecords={currentContainers?.length}
            emptyMessage={i18n.t('InventoryCheck:emptyTable')}
          >
            <Column
              headerStyle={{ width: '200px' }}
              body={(c: Container) => renderStatus(c.seenNext !== null)}
              header={i18n.t('Common:status')}
            />
            <Column
              headerStyle={{ width: '200px' }}
              body={(c: Container) => {
                const containerType = containerTypes.find(
                  (ct) => ct.id === c.typeId,
                );

                return renderCell(containerType?.name, false);
              }}
              header={i18n.t('InventoryCheck:containerType')}
            />
            <Column
              headerStyle={{ width: '200px' }}
              body={(c: Container) => renderContainerName(c.id, c.name)}
              header={i18n.t('Common:name')}
            />
            <Column
              headerStyle={{ width: '200px' }}
              body={(c: Container) =>
                renderDate(c?.lastSeenAtStoreBeforeExpiration || '')
              }
              header={i18n.t('InventoryCheck:bookedAt')}
            />
            {showPartnerData && (
              <Column
                headerStyle={{ width: '200px' }}
                body={(c: Container) => renderDate(c?.seenNext || '')}
                header={i18n.t('InventoryCheck:seenNextAt')}
              />
            )}
          </DataTable>
        </>
      )}
    </ContentSection>
  );
};

export default InventoryCheckTabLostBowls;
